<script lang="ts" setup>
import { Head } from "@inertiajs/vue3";
import { DateTime } from "luxon";
import { Link } from "@inertiajs/vue3";
import { router } from "@inertiajs/vue3";

// import { Duration } from "luxon";
import "/node_modules/flag-icons/css/flag-icons.min.css";

import { Event } from "@/types";

interface Props {
    event: Event;
    ccss: object;
}

const myProps = reactive({
    localRallyDateTime: null,
});

import { computed, inject, onMounted, onUnmounted, reactive } from "vue";

const props = withDefaults(defineProps<Props>(), {});

import { startLocalEventTimeTicker, stopLocalEventTimeTicker } from "@/Helpers/localEventTimeTicker.js";
import LocalEventTime from "@/Components/LocalEventTime.vue";
import LogoBlock from "@/Components/LogoBlock.vue";
import eventBus from "@/Helpers/eventBus";

onMounted(() => {
    // console.log("onMounted EventInformation");

    // do not run local event time ticker in SSR
    if (typeof window !== "undefined") {
        startLocalEventTimeTicker(props.event.timezone);
    }

    fitText(document.getElementById("event_name"), 3, {
        minFontSize: 24,
        maxFontSize: 50,
    });
    fitText(document.getElementById("time"), 0.525);

    fitText(document.getElementById("sm_event_name"), 3, {
        minFontSize: 18,
        maxFontSize: 36,
    });
    fitText(document.getElementById("sm_time"), 0.525);

    // console.log("onMounted LocalEventTime");
    eventBus.$on("updateTimeForTheLocalEventTimeComponent", (args) => {
        myProps.localRallyDateTime = DateTime.now()
            .minus(args.device_ts_ms_offset)
            .setZone(props.event.timezone);

        // console.log(
        //     "myProps.localRallyDateTime.toUnixInteger() % 20",
        //     myProps.localRallyDateTime.toUnixInteger() % 20,
        // );
        if (myProps.localRallyDateTime.toUnixInteger() % 20 === 12) {
            // ik pēc 20 sekundēm datu refresh (lai uzrādās starta laiku izmaiņas)
            // un refresh notiek nevis uz apaļu minūti, bet:
            // sanāks uz :52s pirms apaļas minūtes un tad uz :12s pēc apaļās minūtes
            // lai paspēj zaļš vārds rādīties 10sek pēc starta, savādāk refresh to zaļo uzreiz aizvāc

            // TODO nākotnē uz websockets pārtaisīt
            if (navigator && navigator.onLine) {
                router.reload({ only: ["ccss"] });
            }
        }
    });
});

onUnmounted(() => {
    // console.log("onUnmounted EventInformation");
    // do not run local event time ticker in SSR
    if (typeof window !== "undefined") {
        stopLocalEventTimeTicker();
    }

    // console.log("onUnmounted LocalEventTime");
    eventBus.$off("updateTimeForTheLocalEventTimeComponent");
});

const filteredCCSS = computed(() => {
    // console.log("filteredCCSS");

    if (!myProps.localRallyDateTime) {
        return [];
    }

    const eventTimeNow = myProps.localRallyDateTime.toMillis();

    return props.ccss.filter(function (ccs) {
        ccs.additonalClasses = null;

        if (!ccs.competitor) {
            return false;
        }

        if (!ccs.planned_start_at) {
            return false;
        }
        const ccsPlannedStartAt = DateTime.fromISO(ccs.planned_start_at)
            .setZone(props.event.timezone)
            .toMillis();

        // 30 sek until the start make RED
        if (ccsPlannedStartAt - eventTimeNow < 30000) {
            ccs.additonalClasses = "bg-orange-400 text-white";
        }

        // started = make green
        if (eventTimeNow > ccsPlannedStartAt) {
            ccs.additonalClasses = "bg-green-500 text-white";
        }

        // hide 10s after the start
        if (eventTimeNow - 10000 < ccsPlannedStartAt) {
            return true;
        }
    });
});

/*!
 * FitText.js 1.0 jQuery free version
 *
 * Copyright 2011, Dave Rupert http://daverupert.com
 * Released under the WTFPL license
 * http://sam.zoy.org/wtfpl/
 * Modified by Slawomir Kolodziej http://slawekk.info
 *
 * Date: Tue Aug 09 2011 10:45:54 GMT+0200 (CEST)
 */
const addEvent = function (el, type, fn) {
    if (el.addEventListener) el.addEventListener(type, fn, false);
    else el.attachEvent("on" + type, fn);
};

const extend = function (obj, ext) {
    for (const key in ext) if (ext.hasOwnProperty(key)) obj[key] = ext[key];
    return obj;
};

const fitText = function (el, kompressor, options) {
    const settings = extend(
        {
            minFontSize: -1 / 0,
            maxFontSize: 1 / 0,
        },
        options,
    );

    const fit = function (el) {
        const compressor = kompressor || 1;

        const resizer = function () {
            el.style.fontSize =
                Math.max(
                    Math.min(el.clientWidth / (compressor * 10), parseFloat(settings.maxFontSize)),
                    parseFloat(settings.minFontSize),
                ) + "px";
        };

        // Call once to set.
        resizer();

        // Bind events
        // If you have any js library which support Events, replace this part
        // and remove addEvent function (or use original jQuery version)
        addEvent(window, "resize", resizer);
        addEvent(window, "orientationchange", resizer);
    };

    if (el.length) for (let i = 0; i < el.length; i++) fit(el[i]);
    else fit(el);

    // return set of elements
    return el;
};
</script>

<template>
    <Head>
        <title>Event start list clock: {{ $page.props.event.name }}</title>

        <meta
            property="og:title"
            head-key="og:title"
            :content="`Event start list clock: ${$page.props.meta_event_name}`"
        />
    </Head>

    <div class="hidden h-screen xl:flex">
        <div class="w-[36rem] shrink-0 overflow-y-hidden border-r-4 p-4 pt-2 2xl:w-[48rem]">
            <table class="w-full table-fixed">
                <thead>
                    <tr
                        class="text-left text-3xl font-semibold"
                        :class="filteredCCSS.length === 0 ? 'border-b-2' : ''"
                    >
                        <th class="w-28 py-2.5 pr-6 2xl:w-32 2xl:py-3">No.</th>
                        <th class="w-44 py-2 pr-6 leading-6 xl:py-2.5 2xl:py-3">
                            Start <br />
                            time
                        </th>
                        <th class="py-2 pr-6 xl:py-2.5 2xl:py-3">Competitors ({{ filteredCCSS.length }})</th>
                    </tr>
                </thead>
                <TransitionGroup
                    tag="tbody"
                    name="ccs-row"
                >
                    <tr
                        v-if="filteredCCSS.length === 0 && myProps.localRallyDateTime"
                        key="empty"
                    >
                        <td colspan="3">
                            <div
                                class="max-w-lg py-20 pr-6 text-center text-4xl font-bold tabular-nums text-orange-400"
                            >
                                There are no competitors with planned start time
                            </div>
                        </td>
                    </tr>
                    <tr
                        v-for="(ccs, index) in filteredCCSS"
                        :key="`c-${ccs.id}`"
                        :class="[ccs.additonalClasses, { 'first-row': index === 0 }]"
                        class="border-t border-gray-400"
                    >
                        <td
                            class="py-2.5 pr-6 text-4xl font-bold tabular-nums 2xl:py-3 2xl:text-5xl"
                            :class="ccs.additonalClasses ? '' : 'text-orange-400'"
                        >
                            {{ ccs.competitor.competitor_number }}
                        </td>
                        <td class="py-2.5 pr-6 font-bold tabular-nums 2xl:py-3">
                            <span
                                v-if="!ccs.planned_start_at"
                                class="text-4xl"
                                >-</span
                            >
                            <span v-else>
                                <span
                                    v-if="
                                        DateTime.fromISO(ccs.planned_start_at)
                                            .setZone(event.timezone)
                                            .hasSame(myProps.localRallyDateTime, 'day')
                                    "
                                    class="text-4xl"
                                >
                                    {{
                                        DateTime.fromISO(ccs.planned_start_at)
                                            .setZone(event.timezone)
                                            .toLocaleString(DateTime.TIME_24_SIMPLE)
                                    }}
                                </span>
                                <span
                                    v-else
                                    class="text-4xl"
                                >
                                    {{
                                        DateTime.fromISO(ccs.planned_start_at)
                                            .setZone(event.timezone)
                                            .toLocaleString(DateTime.DATETIME_SHORT)
                                    }}
                                </span>
                            </span>
                        </td>
                        <td
                            class="break-words py-2.5 pr-4 text-3xl font-semibold 2xl:py-3 2xl:text-4xl"
                            :class="ccs.additonalClasses ? '' : 'text-gray-700'"
                        >
                            <span
                                :class="`mr-4 border  fi fi-${ccs.competitor.driver_country.toLowerCase()}`"
                            ></span>
                            <span
                                v-if="ccs.competitor.co_driver_country"
                                :class="`mr-5 border  fi fi-${ccs.competitor.co_driver_country.toLowerCase()}`"
                            ></span>
                            <template v-if="ccs.competitor.co_driver_country && ccs.competitor.co_driver_name">
                                {{ ccs.competitor.driver_name.charAt(0) }}.{{ ccs.competitor.driver_surname }}
                                <br />{{ ccs.competitor.co_driver_name.charAt(0) }}.{{
                                    ccs.competitor.co_driver_surname
                                }}
                            </template>
                            <template v-else>{{ ccs.competitor.driver_full_name }}</template>
                        </td>
                    </tr>
                </TransitionGroup>
            </table>
        </div>
        <div class="flex-1 text-center text-xl">
            <div class="flex h-full flex-col">
                <div
                    id="event_name"
                    class="p-8 pb-2 text-5xl text-gray-500 lg:text-6xl"
                >
                    <Link
                        class="font-normal text-gray-500 visited:text-gray-500 hover:text-blue-600"
                        :href="
                            route('event.public.start-list', {
                                hashid: event.hashid,
                                seo_slug: event.seo_slug,
                            })
                        "
                    >
                        {{ event.name }}
                    </Link>
                </div>
                <div class="grid flex-1 content-center">
                    <div
                        id="time"
                        class="-mt-14 select-none px-2 font-semibold"
                    >
                        <LocalEventTime
                            :show-u-t-c="false"
                            luxon-preset="TIME_24_WITH_SECONDS"
                        />
                    </div>
                </div>
            </div>
            <div class="absolute bottom-2 right-4">
                <LogoBlock :is-beta="false" />
            </div>
        </div>
    </div>

    <div class="flex h-screen xl:hidden">
        <div class="flex-1 text-center text-xl">
            <div class="flex h-full flex-col">
                <div
                    id="sm_event_name"
                    class="p-4 pb-2 text-4xl text-gray-500"
                >
                    <Link
                        class="font-normal text-gray-500 visited:text-gray-500 hover:text-blue-600"
                        :href="
                            route('event.public.start-list', {
                                hashid: event.hashid,
                                seo_slug: event.seo_slug,
                            })
                        "
                    >
                        {{ event.name }}
                    </Link>
                </div>
                <div class="flex-1">
                    <div
                        id="sm_time"
                        class="-mt-8 select-none px-2 py-28 font-semibold sm:py-36"
                    >
                        <LocalEventTime
                            :show-u-t-c="false"
                            luxon-preset="TIME_24_WITH_SECONDS"
                        />
                    </div>
                    <div class="overflow-y-hidden border-t-4 p-4 pt-2">
                        <table class="w-full table-fixed">
                            <thead>
                                <tr
                                    class="text-left text-xl font-semibold sm:text-3xl"
                                    :class="filteredCCSS.length === 0 ? 'border-b-2' : ''"
                                >
                                    <th class="w-20 py-2 pr-2 sm:w-32 sm:pr-6">No.</th>
                                    <th class="w-24 py-2 pr-2 leading-6 sm:w-44 sm:pr-6 sm:leading-7">
                                        Start <br />
                                        time
                                    </th>
                                    <th class="py-2">
                                        Competitors ({{ filteredCCSS.length }})
                                    </th>
                                </tr>
                            </thead>
                            <TransitionGroup
                                tag="tbody"
                                name="ccs-row"
                            >
                                <tr
                                    v-if="filteredCCSS.length === 0 && myProps.localRallyDateTime"
                                    key="empty"
                                >
                                    <td colspan="3">
                                        <div
                                            class="max-w-lg py-10 pr-6 text-center text-2xl font-bold tabular-nums text-orange-400"
                                        >
                                            There are no competitors with planned start time
                                        </div>
                                    </td>
                                </tr>
                                <tr
                                    v-for="(ccs, index) in filteredCCSS"
                                    :key="`c-${ccs.id}`"
                                    :class="[ccs.additonalClasses, { 'first-row': index === 0 }]"
                                    class="border-t border-gray-400 text-2xl sm:text-4xl"
                                >
                                    <td
                                        class="py-4 pr-2 text-2xl font-bold tabular-nums sm:py-8 sm:pr-3 sm:text-5xl"
                                        :class="ccs.additonalClasses ? '' : 'text-orange-400'"
                                    >
                                        {{ ccs.competitor.competitor_number }}
                                    </td>
                                    <td class="py-2 pr-2 font-bold tabular-nums sm:pr-6">
                                        <span v-if="!ccs.planned_start_at">-</span>
                                        <span v-else>
                                            <span
                                                v-if="
                                                    DateTime.fromISO(ccs.planned_start_at)
                                                        .setZone(event.timezone)
                                                        .hasSame(myProps.localRallyDateTime, 'day')
                                                "
                                                class="text-2xl sm:text-4xl"
                                            >
                                                {{
                                                    DateTime.fromISO(ccs.planned_start_at)
                                                        .setZone(event.timezone)
                                                        .toLocaleString(DateTime.TIME_24_SIMPLE)
                                                }}
                                            </span>
                                            <span
                                                v-else
                                                class="text-2xl sm:text-4xl"
                                            >
                                                {{
                                                    DateTime.fromISO(ccs.planned_start_at)
                                                        .setZone(event.timezone)
                                                        .toLocaleString(DateTime.DATETIME_SHORT)
                                                }}
                                            </span>
                                        </span>
                                    </td>
                                    <td
                                        class="break-words py-2 text-left text-2xl font-semibold sm:text-4xl md:pr-4 md:text-5xl"
                                        :class="ccs.additonalClasses ? '' : 'text-gray-700'"
                                    >
                                        <span
                                            :class="`mr-2 border sm:mr-4 fi fi-${ccs.competitor.driver_country.toLowerCase()}`"
                                        ></span>
                                        <span
                                            v-if="ccs.competitor.co_driver_country"
                                            :class="`mr-2 border sm:mr-5 fi fi-${ccs.competitor.co_driver_country.toLowerCase()}`"
                                        ></span>
                                        <template
                                            v-if="
                                                ccs.competitor.co_driver_country &&
                                                ccs.competitor.co_driver_name
                                            "
                                        >
                                            {{ ccs.competitor.driver_name.charAt(0) }}.{{
                                                ccs.competitor.driver_surname
                                            }}
                                            <br />{{ ccs.competitor.co_driver_name.charAt(0) }}.{{
                                                ccs.competitor.co_driver_surname
                                            }}
                                        </template>
                                        <template v-else>{{ ccs.competitor.driver_full_name }}</template>
                                    </td>
                                </tr>
                            </TransitionGroup>
                        </table>
                        <div class="my-16">
                            <LogoBlock :is-beta="false" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.first-row > td {
    zoom: 1.2;
}
.ccs-row-move,
.ccs-row-leave-active {
    transition: all 2s ease;
}
.ccs-row-leave-active {
    position: absolute;
}
.ccs-row-leave-to {
    opacity: 0;
    transform: translateX(-100%);
}
</style>
